/**
 * 画像処理ユーティリティ
 */

// 最大サイズ（2K = 2048px）
const MAX_IMAGE_SIZE = 2048;

/**
 * 画像を指定サイズ以下にダウンスケール
 * 元画像が指定サイズ以下の場合はそのまま返す
 */
export function downscaleImage(
  file: File,
  maxSize: number = MAX_IMAGE_SIZE
): Promise<{ base64: string; mimeType: string }> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const reader = new FileReader();

    reader.onload = () => {
      const dataUrl = reader.result as string;

      img.onload = () => {
        const { width, height } = img;

        // リサイズが必要かチェック
        if (width <= maxSize && height <= maxSize) {
          // リサイズ不要、元のデータをそのまま返す
          const base64 = dataUrl.split(',')[1];
          resolve({ base64, mimeType: file.type });
          return;
        }

        // アスペクト比を保持してリサイズ
        let newWidth: number;
        let newHeight: number;

        if (width > height) {
          newWidth = maxSize;
          newHeight = Math.round((height / width) * maxSize);
        } else {
          newHeight = maxSize;
          newWidth = Math.round((width / height) * maxSize);
        }

        // Canvasでリサイズ
        const canvas = document.createElement('canvas');
        canvas.width = newWidth;
        canvas.height = newHeight;

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Canvas context not available'));
          return;
        }

        // 高品質リサイズ
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(img, 0, 0, newWidth, newHeight);

        // PNG形式で出力（品質維持）
        const resizedDataUrl = canvas.toDataURL('image/png');
        const base64 = resizedDataUrl.split(',')[1];

        resolve({ base64, mimeType: 'image/png' });
      };

      img.onerror = () => {
        reject(new Error('Failed to load image'));
      };

      img.src = dataUrl;
    };

    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };

    reader.readAsDataURL(file);
  });
}

/**
 * Base64画像をダウンスケール
 *
 * options で出力形式を指定できる (既定は従来どおり PNG)。
 * AI 解析に渡すだけの画像など、可逆である必要がない用途では
 * { outputMimeType: 'image/jpeg', quality: 0.85 } を渡すと転送量を大きく減らせる。
 */
export function downscaleBase64Image(
  base64: string,
  mimeType: string,
  maxSize: number = MAX_IMAGE_SIZE,
  options?: { outputMimeType?: string; quality?: number }
): Promise<{ base64: string; mimeType: string }> {
  const outputMimeType = options?.outputMimeType ?? 'image/png';

  return new Promise((resolve, reject) => {
    const img = new Image();

    img.onload = () => {
      const { width, height } = img;

      // リサイズも形式変換も不要ならそのまま返す
      if (width <= maxSize && height <= maxSize && mimeType === outputMimeType) {
        resolve({ base64, mimeType });
        return;
      }

      // アスペクト比を保持してリサイズ (既に maxSize 以下なら等倍のまま形式だけ変換)
      let newWidth = width;
      let newHeight = height;

      if (width > maxSize || height > maxSize) {
        if (width > height) {
          newWidth = maxSize;
          newHeight = Math.round((height / width) * maxSize);
        } else {
          newHeight = maxSize;
          newWidth = Math.round((width / height) * maxSize);
        }
      }

      // Canvasでリサイズ
      const canvas = document.createElement('canvas');
      canvas.width = newWidth;
      canvas.height = newHeight;

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Canvas context not available'));
        return;
      }

      // JPEG は透過を持てず、透明部分がそのままだと黒く潰れるので白で埋める
      if (outputMimeType !== 'image/png') {
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, newWidth, newHeight);
      }

      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(img, 0, 0, newWidth, newHeight);

      const resizedDataUrl = canvas.toDataURL(outputMimeType, options?.quality);
      const resizedBase64 = resizedDataUrl.split(',')[1];

      resolve({ base64: resizedBase64, mimeType: outputMimeType });
    };

    img.onerror = () => {
      reject(new Error('Failed to load image'));
    };

    img.src = `data:${mimeType};base64,${base64}`;
  });
}
